import { useCallback, useEffect, useRef, useState } from 'react';
import logger from '../utils/Logger';
import { loadReprocessPreference, saveReprocessPreference, clearReprocessPreference } from '../utils/reprocessUtils';

const COMPONENT_NAME = 'useReprocessControl';

/**
 * Custom hook to manage reprocess toggle state and preferences
 * Handles persistence to localStorage and synchronizes ref with state
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableByDefault - Enable reprocess by default (default: false)
 * @param {Function} options.onToggle - Callback when toggle changes (optional)
 * @returns {Object} Reprocess control object with state, ref, and toggle function
 */
export function useReprocessControl(options = {}) {
  const { enableByDefault = false, onToggle = null } = options;
 const [reprocessEnabled, setReprocessEnabled] = useState(false);
  const reprocessEnabledRef = useRef(false);

  // Load persisted reprocess preference on mount
  useEffect(() => {
    (async () => {
      try {
        const stored = await loadReprocessPreference();
        setReprocessEnabled(stored !== undefined ? stored : enableByDefault);
        reprocessEnabledRef.current = stored !== undefined ? stored : enableByDefault;
        logger.debug('Reprocess preference loaded on mount', {
          component: COMPONENT_NAME,
          action: 'LOAD_ON_MOUNT',
          context: {
            stored,
            enableByDefault,
            final: stored !== undefined ? stored : enableByDefault,
          },
        });
      } catch (e) {
        logger.warn('Failed to load reprocess preference on mount', {
          component: COMPONENT_NAME,
          action: 'LOAD_ON_MOUNT_FAILED',
          error: e,
        });
        console.warn('[useReprocessControl] Failed to load preference:', e?.message || e);
        setReprocessEnabled(enableByDefault);
        reprocessEnabledRef.current = enableByDefault;
      }
    })();
  }, [enableByDefault]);

  // Keep ref in sync with state so callbacks read the latest value
  useEffect(() => {
    reprocessEnabledRef.current = reprocessEnabled;
  }, [reprocessEnabled]);

  /**
   * Toggle the reprocess enabled state
   */
  const toggleReprocess = useCallback(() => {
    setReprocessEnabled(prev => {
      const next = !prev;
      reprocessEnabledRef.current = next;

      logger.info('Reprocess toggle changed', {
        component: COMPONENT_NAME,
        action: 'TOGGLE_REPROCESS',
        context: {
          previousState: prev,
          newState: next,
        },
      });

      // Persist to localStorage
      saveReprocessPreference(next).catch(err => {
        logger.error('Failed to persist reprocess preference', {
          component: COMPONENT_NAME,
          action: 'PERSIST_PREFERENCE_FAILED',
          error: err,
        });
        console.warn('[useReprocessControl] Failed to persist preference:', err?.message || err);
      });

      // Call optional callback
      if (onToggle) {
        try {
          onToggle(next);
        } catch (err) {
          logger.error('onToggle callback failed', {
            component: COMPONENT_NAME,
            action: 'TOGGLE_CALLBACK_ERROR',
            error: err,
          });
          console.error('[useReprocessControl] onToggle callback error:', err);
        }
      }

      console.log('[useReprocessControl] Toggled →', next);
      return next;
    });
  }, [onToggle]);

  /**
   * Manually set the reprocess enabled state
   */
  const setReprocessEnabledState = useCallback((enabled) => {
    setReprocessEnabled(enabled);
    reprocessEnabledRef.current = enabled;

    logger.debug('Reprocess state set manually', {
      component: COMPONENT_NAME,
      action: 'SET_STATE',
      context: { enabled },
    });

    saveReprocessPreference(enabled).catch(err => {
      logger.error('Failed to persist reprocess preference after manual set', {
        component: COMPONENT_NAME,
        action: 'PERSIST_PREFERENCE_FAILED',
        error: err,
      });
    });
  }, []);

  /**
   * Reset reprocess preference to default
   */
  const resetReprocess = useCallback(async () => {
    try {
      await clearReprocessPreference();
      setReprocessEnabled(enableByDefault);
      reprocessEnabledRef.current = enableByDefault;

      logger.info('Reprocess preference reset to default', {
        component: COMPONENT_NAME,
        action: 'RESET_PREFERENCE',
        context: { enableByDefault },
      });
    } catch (err) {
      logger.error('Failed to reset reprocess preference', {
        component: COMPONENT_NAME,
        action: 'RESET_PREFERENCE_FAILED',
        error: err,
      });
      console.error('[useReprocessControl] Failed to reset:', err?.message || err);
    }
  }, [enableByDefault]);

  return {
    reprocessEnabled,
    reprocessEnabledRef,
    toggleReprocess,
    setReprocessEnabledState,
    resetReprocess,
  };
}

export default useReprocessControl;
